export interface DurableMutationTransportIdentity {
  userId: string;
  tenantId: string;
}

export function resolveDurableMutationTenant(
  activeTenantId: string | undefined,
  expected: DurableMutationTransportIdentity | undefined,
): string | undefined {
  // Mono-tenant installations historically have no local workspace hint.
  // During a leased command the server-resolved member tenant is therefore
  // the explicit fallback. A real stored switch remains authoritative so the
  // identity guard can reject B while the command still belongs to A.
  return activeTenantId ?? expected?.tenantId;
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

function isPostgrestRequest(input: RequestInfo | URL): boolean {
  try {
    return new URL(requestUrl(input)).pathname.startsWith('/rest/v1/');
  } catch {
    return false;
  }
}

function jwtSubject(authorization: string | null): string | undefined {
  if (!authorization?.startsWith('Bearer ')) return undefined;
  const payloadSegment = authorization
    .slice('Bearer '.length)
    .trim()
    .split('.')[1];
  if (!payloadSegment || typeof globalThis.atob !== 'function') {
    return undefined;
  }
  try {
    const normalized = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      '=',
    );
    const binary = globalThis.atob(padded);
    const bytes = Uint8Array.from(binary, (character) =>
      character.charCodeAt(0),
    );
    const payload = JSON.parse(new TextDecoder().decode(bytes)) as Record<
      string,
      unknown
    >;
    const subject = payload['sub'];
    return typeof subject === 'string' && subject.length > 0
      ? subject
      : undefined;
  } catch {
    return undefined;
  }
}

export function isDurableMutationTransportAuthorized(input: {
  request: RequestInfo | URL;
  headers: Headers;
  activeTenantId: string | undefined;
  expected: DurableMutationTransportIdentity;
}): boolean {
  if (!isPostgrestRequest(input.request)) return true;
  return (
    input.activeTenantId === input.expected.tenantId &&
    jwtSubject(input.headers.get('authorization')) === input.expected.userId
  );
}
